import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ShoppingCart } from 'lucide-react'
import type { Product } from '#bv/data/products'
import { DisplayPrice } from '#bv/components/DisplayPrice'
import { useCurrency } from '#bv/lib/currency/CurrencyContext'
import { addToCart, setCartItems, type CartItem } from '#bv/lib/cart'
import {
  allowedServerBoostPresets,
  normalizeDiscordServerLink,
  quoteServerBoostPurchase,
  SERVER_BOOST_BEST_VALUE_BOOSTS,
  serverBoostCartLineName,
  validateDiscordServerLinkMessage,
} from '#bv/lib/serverBoostProduct'
import { useProductStock } from '#bv/lib/useProductStock'

type Variant = 'default' | 'compact' | 'sheet'

type Props = {
  product: Product & { serverBoostConfig: NonNullable<Product['serverBoostConfig']> }
  /** @deprecated Use `variant="compact"` */
  compact?: boolean
  variant?: Variant
}

const pillBase =
  'inline-flex w-full min-h-[2.75rem] items-center justify-center gap-2 rounded-full font-semibold tracking-wide transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00bfff]/45 disabled:cursor-not-allowed disabled:opacity-45 active:scale-[0.98]'

export function ServerBoostProductPurchase({ product, compact: compactProp, variant: variantProp }: Props) {
  const variant: Variant = variantProp ?? (compactProp ? 'compact' : 'default')
  const { formatUsdCharge } = useCurrency()
  const navigate = useNavigate()
  const { inStock } = useProductStock()
  const cfg = product.serverBoostConfig
  const presetBoosts = useMemo(() => allowedServerBoostPresets(cfg), [cfg])
  const defaultBoost =
    presetBoosts.includes(SERVER_BOOST_BEST_VALUE_BOOSTS)
      ? SERVER_BOOST_BEST_VALUE_BOOSTS
      : (presetBoosts[0] ?? SERVER_BOOST_BEST_VALUE_BOOSTS)
  const [selectedBoost, setSelectedBoost] = useState(defaultBoost)
  const [inviteInput, setInviteInput] = useState('')
  const [fieldError, setFieldError] = useState('')
  const [addedFlash, setAddedFlash] = useState(false)
  const productInStock = inStock(product.id)

  const bestValueQuote = useMemo(
    () => quoteServerBoostPurchase(SERVER_BOOST_BEST_VALUE_BOOSTS, cfg),
    [cfg],
  )

  const quote = useMemo(() => quoteServerBoostPurchase(selectedBoost, cfg), [selectedBoost, cfg])

  const packageError = useMemo(() => {
    if (!presetBoosts.includes(selectedBoost)) return 'Choose a boost package.'
    return null
  }, [presetBoosts, selectedBoost])

  const serverLinkError = useMemo(() => validateDiscordServerLinkMessage(inviteInput), [inviteInput])

  const lineItem = (): CartItem | null => {
    if (packageError || serverLinkError) return null
    const invite = normalizeDiscordServerLink(inviteInput)
    if (!invite) return null
    return {
      productId: product.id,
      name: serverBoostCartLineName(quote, cfg),
      unitPrice: quote.usdPrice,
      quantity: 1,
      serverBoostCount: quote.boostCount,
      serverBoostInviteLink: invite,
    }
  }

  const runCheckout = (action: 'cart' | 'buy') => {
    if (!productInStock) return
    if (packageError) {
      setFieldError(packageError)
      return
    }
    if (serverLinkError) {
      setFieldError(serverLinkError)
      return
    }
    const item = lineItem()
    if (!item) {
      setFieldError('Select a package and paste your Discord server link.')
      return
    }
    setFieldError('')
    if (action === 'cart') {
      addToCart(item, 1)
      setAddedFlash(true)
      window.setTimeout(() => setAddedFlash(false), 1600)
    } else {
      setCartItems([item])
      void navigate({ to: '/cart', search: { checkout: '1' } })
    }
  }

  const disabled = Boolean(packageError || serverLinkError)
  const errorMsg = fieldError || packageError || serverLinkError

  if (variant === 'sheet') {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="sb-tier-grid" role="listbox" aria-label="Boost quantity">
          {presetBoosts.map((n) => {
            const isBest = n === SERVER_BOOST_BEST_VALUE_BOOSTS
            const tierPrice = quoteServerBoostPurchase(n, cfg).usdPrice
            const selected = selectedBoost === n
            return (
              <button
                key={n}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  setSelectedBoost(n)
                  setFieldError('')
                }}
                className={`sb-tier-btn ${selected ? 'sb-tier-btn--selected' : ''} ${isBest ? 'sb-tier-btn--best' : ''}`}
              >
                {isBest ? <span className="sb-tier-btn__badge">Best</span> : null}
                <span className="sb-tier-btn__count">{n}×</span>
                <span className="sb-tier-btn__price">
                  <DisplayPrice usd={tierPrice} />
                </span>
              </button>
            )
          })}
        </div>

        <p className="sb-checkout-hint">
          <strong>{SERVER_BOOST_BEST_VALUE_BOOSTS}×</strong> unlocks Discord Level 3 ·{' '}
          <DisplayPrice usd={bestValueQuote.usdPrice} className="font-semibold text-[#d4e8ff]" />
        </p>

        <div className="sb-checkout-field">
          <label htmlFor="sb-invite">
            Server invite <span className="text-[#93c5fd]">*</span>
          </label>
          <input
            id="sb-invite"
            type="url"
            inputMode="url"
            required
            value={inviteInput}
            onChange={(e) => {
              setInviteInput(e.target.value)
              setFieldError('')
            }}
            className="bv-input rounded-xl px-3 text-sm text-[#f0f6ff]"
            placeholder="discord.gg/…"
            autoComplete="off"
          />
        </div>

        {errorMsg ? (
          <p className="mt-2 text-xs font-medium text-[#fda4af]" role="alert">
            {errorMsg}
          </p>
        ) : null}

        <div className="sb-checkout-actions">
          {!productInStock ? (
            <button type="button" disabled className={`${pillBase} sb-checkout-buy border border-white/12 bg-white/[0.03] text-[#5a6d8a]`}>
              Out of stock
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => runCheckout('buy')}
                disabled={disabled}
                className={`btn-primary ${pillBase} sb-checkout-buy`}
              >
                Buy for <DisplayPrice usd={quote.usdPrice} className="ml-1 font-bold" />
              </button>
              <button
                type="button"
                onClick={() => runCheckout('cart')}
                disabled={disabled}
                className={`sb-checkout-cart ${addedFlash ? 'text-emerald-300' : ''}`}
              >
                {addedFlash ? 'Added to cart ✓' : 'Add to cart'}
              </button>
            </>
          )}
          <p className="sb-checkout-note">{product.deliveryInfo}</p>
        </div>
      </div>
    )
  }

  const compact = variant === 'compact'
  const pad = compact
    ? 'py-2.5 text-xs sm:py-3 sm:text-sm'
    : 'py-3 text-sm sm:min-h-[2.875rem] sm:text-[0.9375rem]'

  const shellClass = `rounded-2xl border border-white/[0.08] bg-gradient-to-b from-white/[0.04] to-black/25 ${
    compact ? 'p-3' : 'p-4 sm:p-5'
  }`

  return (
    <div className={shellClass}>
      <p className="m-0 text-[0.62rem] font-bold uppercase tracking-[0.16em] text-[#7a92b8]">Choose a package</p>
      <div
        className="mt-3 rounded-xl border border-[#00bfff]/35 bg-gradient-to-r from-[rgba(0, 191, 255,0.12)] to-[rgba(88,101,242,0.1)] px-3.5 py-3"
        role="note"
      >
        <p className="m-0 text-xs font-bold uppercase tracking-[0.14em] text-[#93c5fd]">⭐ Best Value</p>
        <p className="m-0 mt-1 text-sm font-semibold text-[#f0f6ff]">
          {SERVER_BOOST_BEST_VALUE_BOOSTS} boosts — unlock Discord Level 3
        </p>
        <p className="m-0 mt-1 text-xs text-[#a8c4e8]">
          <DisplayPrice usd={bestValueQuote.usdPrice} className="font-semibold text-[#d4e8ff]" />{' '}
          <span className="text-[#5a6d8a]">({formatUsdCharge(bestValueQuote.usdPrice)} USD)</span>
        </p>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {presetBoosts.map((n) => {
          const isBest = n === SERVER_BOOST_BEST_VALUE_BOOSTS
          const tierPrice = quoteServerBoostPurchase(n, cfg).usdPrice
          const selected = selectedBoost === n
          return (
            <button
              key={n}
              type="button"
              onClick={() => {
                setSelectedBoost(n)
                setFieldError('')
              }}
              className={`flex min-h-[3.25rem] w-full flex-col items-start justify-center rounded-xl border px-3.5 py-2.5 text-left transition-colors ${
                selected
                  ? isBest
                    ? 'border-[#00bfff] bg-[rgba(0, 191, 255,0.18)] shadow-[0_0_24px_rgba(0, 191, 255,0.12)]'
                    : 'border-[#00bfff] bg-[rgba(0, 191, 255,0.12)]'
                  : isBest
                    ? 'border-[#00bfff]/45 bg-[rgba(0, 191, 255,0.06)] hover:border-[#00bfff]'
                    : 'border-white/12 bg-white/[0.02] hover:border-[#00bfff]/35'
              }`}
            >
              <span className="text-sm font-bold text-[#f0f6ff]">
                {n}×
                {isBest ? (
                  <span className="ml-1 text-[0.65rem] font-extrabold uppercase tracking-wide text-[#93c5fd]">
                    ⭐ Best Value
                  </span>
                ) : null}
              </span>
              <span className="mt-0.5 text-xs font-semibold tabular-nums text-[#d4e8ff]">
                <DisplayPrice usd={tierPrice} />
                <span className="ml-1 font-medium text-[#5a6d8a]">({formatUsdCharge(tierPrice)})</span>
              </span>
            </button>
          )
        })}
      </div>

      <label className="mt-4 block text-[0.62rem] font-bold uppercase tracking-[0.16em] text-[#7a92b8]">
        Discord server link <span className="text-[#93c5fd]">*</span>
        <input
          type="url"
          inputMode="url"
          required
          value={inviteInput}
          onChange={(e) => {
            setInviteInput(e.target.value)
            setFieldError('')
          }}
          className="bv-input mt-2 h-11 w-full rounded-xl px-3 text-sm text-[#f0f6ff] sm:h-12"
          placeholder="https://discord.gg/your-server"
          autoComplete="off"
        />
      </label>
      <p className="mt-2 text-[0.7rem] leading-relaxed text-[#5a6d8a]">
        Required — paste a permanent invite (discord.gg/…) or your server channel link (discord.com/channels/…).
        Create an invite under Server Settings → Invites (never expires).
      </p>

      {!packageError ? (
        <dl className="mt-4 space-y-2.5 rounded-xl border border-[rgba(88,101,242,0.35)] bg-[rgba(88,101,242,0.08)] px-3.5 py-3 text-sm">
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-[#7a92b8]">Package</dt>
            <dd className="font-display text-lg font-bold tabular-nums text-[#f0f6ff]">{quote.boostCount}×</dd>
          </div>
          <div className="flex items-baseline justify-between gap-3 border-t border-white/[0.08] pt-2.5">
            <dt className="font-semibold text-[#8ecfff]">You pay</dt>
            <dd className="font-display text-xl font-bold tabular-nums text-[#93c5fd]">
              <DisplayPrice usd={quote.usdPrice} />
              <span className="ml-1 text-xs font-medium text-[#5a6d8a]">({formatUsdCharge(quote.usdPrice)} USD)</span>
            </dd>
          </div>
        </dl>
      ) : null}

      {errorMsg ? (
        <p className="mt-3 text-xs font-medium text-[#fda4af]" role="alert">
          {errorMsg}
        </p>
      ) : null}

      {!productInStock ? (
        <button
          type="button"
          disabled
          className={`${pillBase} ${pad} mt-4 border border-white/12 bg-white/[0.03] text-[#5a6d8a]`}
        >
          Out of stock
        </button>
      ) : (
        <div className="mt-4">
          <div className={`flex w-full gap-2.5 flex-col ${compact ? '' : 'sm:flex-row sm:gap-3'}`}>
            <button
              type="button"
              onClick={() => runCheckout('buy')}
              disabled={disabled}
              className={`btn-primary ${pillBase} ${pad} flex-1 shadow-[0_6px_28px_rgba(0, 191, 255,0.35)]`}
            >
              Buy now
            </button>
            <button
              type="button"
              onClick={() => runCheckout('cart')}
              disabled={disabled}
              className={`${pillBase} ${pad} flex-1 border border-white/20 bg-white/[0.03] text-[#f0f6ff] hover:border-[rgba(0, 191, 255,0.45)] hover:bg-[rgba(0, 191, 255,0.1)] ${
                addedFlash ? 'ring-2 ring-emerald-400/45' : ''
              }`}
            >
              <ShoppingCart className="h-[1.1em] w-[1.1em] shrink-0 opacity-90" strokeWidth={2} aria-hidden />
              {addedFlash ? 'Added' : 'Add to cart'}
            </button>
          </div>
          <p className="mt-2 text-center text-[0.62rem] leading-relaxed sm:text-left" style={{ color: '#5a6d8a' }}>
            Payment processor and store fees may apply at checkout.
          </p>
        </div>
      )}
    </div>
  )
}
